import axios from "axios";
import { BACKEND_URL } from "../../../utils/api";
import { Payment } from "../../../utils/database";

export interface PaginatedPaymentsResponse {
  payments: Payment[];
  total: number;
  skip: number;
  limit: number;
}

export interface PaymentStats {
  category: string;
  total_amount: number;
  total_fees: number;
  total_transactions: number;
  succeeded_count: number;
  failed_count: number;
  pending_count: number;
  succeeded_amount: number;
  failed_amount: number;
  pending_amount: number;
  unique_users: number;
  date?: string;
}

interface PaginationParams {
  skip?: number;
  limit?: number;
  search?: string;
  startDate?: string;
  endDate?: string;
  senderCountryId?: number;
  receiverCountryId?: number;
}

export interface DailyByOperatorStats {
  date: string;
  operator: string;
  transaction_count: number;
  total_cad: number;
  total_fcfa: number;
}

export interface DailyByOperatorResponse {
  message: string;
  data: DailyByOperatorStats[];
}
export const paymentsService = {
  async getPayments(token: string, pagination?: PaginationParams): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/get_all_payment`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit || undefined,
          search: pagination?.search || undefined,
          start_date: pagination?.startDate || undefined,
          end_date: pagination?.endDate || undefined,
          sender_country_id: pagination?.senderCountryId || undefined,
          receiver_country_id: pagination?.receiverCountryId || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getPendingPayments(token: string, pagination?: PaginationParams): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/get_pending_payments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 20,
          search: pagination?.search || undefined,
          start_date: pagination?.startDate || undefined,
          end_date: pagination?.endDate || undefined,
          sender_country_id: pagination?.senderCountryId || undefined,
          receiver_country_id: pagination?.receiverCountryId || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getSucceededPayoutPending(token: string, pagination?: PaginationParams): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/get_succeeded_payout_pending`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 20,
          search: pagination?.search || undefined,
          start_date: pagination?.startDate || undefined,
          end_date: pagination?.endDate || undefined,
          sender_country_id: pagination?.senderCountryId || undefined,
          receiver_country_id: pagination?.receiverCountryId || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getSucceededPayoutFailed(token: string, pagination?: PaginationParams): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/get_succeeded_payout_failed`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 20,
          search: pagination?.search || undefined,
          start_date: pagination?.startDate || undefined,
          end_date: pagination?.endDate || undefined,
          sender_country_id: pagination?.senderCountryId || undefined,
          receiver_country_id: pagination?.receiverCountryId || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getFailedPayments(token: string, pagination?: PaginationParams): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/get_failed_payments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 20,
          search: pagination?.search || undefined,
          start_date: pagination?.startDate || undefined,
          end_date: pagination?.endDate || undefined,
          sender_country_id: pagination?.senderCountryId || undefined,
          receiver_country_id: pagination?.receiverCountryId || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getSucceededPayoutSuccess(token: string, pagination?: PaginationParams): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/get_succeeded_payout_success`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 20,
          search: pagination?.search || undefined,
          start_date: pagination?.startDate || undefined,
          end_date: pagination?.endDate || undefined,
          sender_country_id: pagination?.senderCountryId || undefined,
          receiver_country_id: pagination?.receiverCountryId || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getTodayPayments(token: string, pagination?: PaginationParams): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/get_today_payments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 20,
          search: pagination?.search || undefined,
          start_date: pagination?.startDate || undefined,
          end_date: pagination?.endDate || undefined,
          sender_country_id: pagination?.senderCountryId || undefined,
          receiver_country_id: pagination?.receiverCountryId || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getPaymentsByDate(
    token: string,
    date: string,
    pagination?: PaginationParams
  ): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/get_payments_by_date`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          date_param: date,
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 20,
          search: pagination?.search || undefined,
          sender_country_id: pagination?.senderCountryId || undefined,
          receiver_country_id: pagination?.receiverCountryId || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async retryFailedPayment(token: string, paymentId: string): Promise<any> {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/payment/send_money_to_failed_payment`,
        { payment_id: paymentId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async changePaymentStatus(
    token: string,
    paymentId: string,
    payoutStatus: string,
    transactionStatus: string
  ): Promise<any> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/change_status_payment/${paymentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          payout_status: payoutStatus,
          transaction_status: transactionStatus,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async getVerificationSides(
    token: string,
    paymentId: string
  ): Promise<{ key: string; label: string; country_code?: string }[]> {
    const response = await axios.get(`${BACKEND_URL}/payment/verification_sides/${paymentId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data?.data?.sides ?? [];
  },

  async verifyOperatorStatus(token: string, paymentId: string, side: string): Promise<any> {
    const response = await axios.get(`${BACKEND_URL}/payment/verify_operator_status/${paymentId}`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { side },
    });
    return response.data?.data ?? response.data;
  },

  async getUserPayments(
    token: string,
    userId: string,
    pagination?: PaginationParams
  ): Promise<PaginatedPaymentsResponse> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/history_of_user`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          user_id: userId,
          skip: pagination?.skip ?? 0,
          limit: pagination?.limit ?? 20,
          search: pagination?.search || undefined,
          start_date: pagination?.startDate || undefined,
          end_date: pagination?.endDate || undefined,
          sender_country_id: pagination?.senderCountryId || undefined,
          receiver_country_id: pagination?.receiverCountryId || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getPaymentStatsByCategory(
    token: string,
    category: string,
    date?: string,
    corridor?: { senderCountryId?: number; receiverCountryId?: number }
  ): Promise<PaymentStats> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/stats/by_category`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          category,
          date_param: date || undefined,
          sender_country_id: corridor?.senderCountryId || undefined,
          receiver_country_id: corridor?.receiverCountryId || undefined,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getDailyStatsByOperator(
    token: string,
    params?: {
      dateFrom?: string;
      dateTo?: string;
      senderCountryId?: number;
      receiverCountryId?: number;
      onlySucceeded?: boolean;
    }
  ): Promise<DailyByOperatorStats[]> {
    try {
      const response = await axios.get(`${BACKEND_URL}/payment/stats/daily_by_operator`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          date_from: params?.dateFrom || undefined,
          date_to: params?.dateTo || undefined,
          sender_country_id: params?.senderCountryId || undefined,
          receiver_country_id: params?.receiverCountryId || undefined,
          only_succeeded: params?.onlySucceeded ?? true,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },
};
